import { Link } from 'react-router-dom'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import styled from '@emotion/styled'
import { Table, Tooltip, Tag, Badge, Popover, Select, Button, Radio, DatePicker } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import locale from 'antd/es/date-picker/locale/pt_BR'
import { TextFieldSearch } from '../../components/TextField'
import { createSimpleStore } from 'react-simple-reducer'
import efiLogo from '../../assets/efi.png'
import hyperpayLogo from '../../assets/hyperpay.png'
import mercadoPagoLogo from '../../assets/mercado_pago.svg'
import Axios from 'axios'
import { MatriculaModel } from '../../models/MatriculaModel'
import { ParceirosModel } from '../../models/ParceirosModel'
import { addDays, format, formatDistance, startOfDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CopyButton } from '../../components/CopyButton'
import { Show } from '../../components/Util'
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseOutlined,
  CopyOutlined,
} from '@ant-design/icons'
import { CopyElement } from '../../components/CopyElement'

const { RangePicker } = DatePicker

const UFS_BRASIL = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
]

const STATUS_MATRICULA_OPTIONS = [
  { value: 'aguardandoPagamento', label: 'Aguard. Pagamento' },
  { value: 'matriculado', label: 'Matriculado' },
  { value: 'finalizado', label: 'Finalizado' },
  { value: 'semCertificado', label: 'Sem Certificado' },
]

const INITIAL_FILTERS = {
  filtroTexto: '',
  statusMatricula: [] as string[],
  estados: [] as string[],
  cursos: [] as number[],
  tipoVenda: 'todos' as 'todos' | 'venda_direta' | 'parceiros',
  parceiroIds: [] as number[],
  provedorPagamento: [] as string[],
  meioPagamento: [] as string[],
  statusPagamento: null as 'pago' | 'pendente' | null,
  dataInicio: null as string | null,
  dataFim: null as string | null,
  tipoData: 'createdAt' as 'createdAt' | 'dataPagamento',
}

const AlunosStore = createSimpleStore(
  {
    parceirosOptions: [] as ParceirosModel[],
    matriculas: [] as MatriculaModel[],
    total: 0 as number,
    valorTotal: null as number | null,
    limit: 15 as number,
    page: 1 as number,
    offset: 0 as number,
    loading: false as boolean,
    filters: INITIAL_FILTERS,
    cursosOptions: [] as Array<{ id: number; nomeReduzido: string }>,
  },
  {
    setParceirosOptions: (state, parceirosOptions: ParceirosModel[]) => ({
      ...state,
      parceirosOptions,
    }),
    setCursosOptions: (state, cursosOptions: Array<{ id: number; nomeReduzido: string }>) => ({
      ...state,
      cursosOptions,
    }),
    setFilters: (state, filters: Partial<typeof INITIAL_FILTERS>) => ({
      ...state,
      filters: { ...state.filters, ...filters },
    }),
    resetFilters: (state) => ({ ...state, filters: INITIAL_FILTERS }),
    setQueryParams: (
      state,
      { page, limit, offset }: { page: number; limit: number; offset: number }
    ) => ({
      ...state,
      page,
      limit,
      offset,
    }),
    fetchAlunosStarted: (state) => ({ ...state, loading: true }),
    fetchAlunosSuccess: (
      state,
      payload: { matriculas: MatriculaModel[]; total: number; valorTotal: number | null }
    ) => ({
      ...state,
      matriculas: payload.matriculas,
      total: payload.total,
      valorTotal: payload.valorTotal,
      loading: false,
    }),
    fetchAlunosFailed: (state) => ({ ...state, loading: false }),
  },
  {
    thunks: {
      getAlunosData(
        filterOverrides: Partial<typeof INITIAL_FILTERS> = {},
        paginationOverride: Partial<{ page: number; limit: number }> = {}
      ) {
        return async (dispatch, getState) => {
          const state = getState()
          const effectiveFilters = { ...state.filters, ...filterOverrides }
          const effectivePage = paginationOverride.page ?? state.page
          const effectiveLimit = paginationOverride.limit ?? state.limit
          const effectiveOffset = (effectivePage - 1) * effectiveLimit

          dispatch(AlunosStore.actions.setFilters(effectiveFilters))
          dispatch(
            AlunosStore.actions.setQueryParams({
              page: effectivePage,
              limit: effectiveLimit,
              offset: effectiveOffset,
            })
          )
          dispatch(AlunosStore.actions.fetchAlunosStarted())

          try {
            const filtroTexto = effectiveFilters.filtroTexto.trim()

            const vendaDireta = effectiveFilters.tipoVenda === 'venda_direta'
            const parceiroIds =
              effectiveFilters.tipoVenda === 'parceiros'
                ? effectiveFilters.parceiroIds.join(',')
                : ''

            const params: any = {
              offset: effectiveOffset,
              limit: effectiveLimit,
              order: 'DESC',
              orderBy: 'createdAt',
              filtroTexto,
              matricula: effectiveFilters.statusMatricula.join(','),
              estado: effectiveFilters.estados.join(','),
              cursos: effectiveFilters.cursos.join(','),
              tipoData: effectiveFilters.tipoData,
            }

            if (vendaDireta) params.vendaDireta = true
            if (effectiveFilters.tipoVenda === 'parceiros') params.somenteParceiros = true
            if (parceiroIds) params.parceiroIds = parceiroIds
            if (effectiveFilters.provedorPagamento.length)
              params.provedorPagamento = effectiveFilters.provedorPagamento.join(',')
            if (effectiveFilters.meioPagamento.length)
              params.meioPagamento = effectiveFilters.meioPagamento.join(',')
            if (effectiveFilters.statusPagamento)
              params.statusPagamento = effectiveFilters.statusPagamento
            if (effectiveFilters.dataInicio) params.dataInicio = effectiveFilters.dataInicio
            if (effectiveFilters.dataFim) params.dataFim = effectiveFilters.dataFim

            const { data } = await Axios.get('matriculas/get-dados-alunos', { params })
            dispatch(AlunosStore.actions.fetchAlunosSuccess(data))
          } catch {
            dispatch(AlunosStore.actions.fetchAlunosFailed())
          }
        }
      },
      getCursosOptions() {
        return async (dispatch) => {
          try {
            const { data } = await Axios.get('/cursos/')
            const cursosFiltered = data.rows
              .filter((c: any) => c.nomeReduzido)
              .sort((a: any, b: any) => a.id - b.id)
              .map((c: any) => ({ id: c.id, nomeReduzido: c.nomeReduzido }))
            dispatch(AlunosStore.actions.setCursosOptions(cursosFiltered))
          } catch {}
        }
      },
      getParceirosOptions() {
        return async (dispatch) => {
          try {
            const { data } = await Axios.get('/parceiros/lista')
            dispatch(AlunosStore.actions.setParceirosOptions(data ?? []))
          } catch {}
        }
      },
    },
  }
)

export const Alunos = () => {
  const init = useCallback((dispatch: any) => {
    dispatch(AlunosStore.thunks.getAlunosData({}, { page: 1, limit: 15 }))
    dispatch(AlunosStore.thunks.getCursosOptions())
    dispatch(AlunosStore.thunks.getParceirosOptions())
  }, [])

  return (
    <AlunosStore.Provider init={init}>
      <PageLayout>
        <PageHeader />
        <div style={{ display: 'flex', gap: 8 }}>
          <FiltersSection />
          <Resultados />
        </div>
        <TabelaContainer />
      </PageLayout>
    </AlunosStore.Provider>
  )
}

function formatValorND(value: number): string {
  if (value >= 1_000_000) {
    return (
      (value / 1_000_000).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + 'M'
    )
  }
  if (value >= 100_000) {
    return (
      (value / 1_000).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + 'K'
    )
  }
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format(value)
    .slice(3)
}

function Resultados() {
  const { total, valorTotal } = AlunosStore.useState()
  return (
    <ResultadosContainer>
      <StatsRow>
        <span style={{ fontSize: 16, color: '#494d50', fontWeight: 600 }}>Resultados</span>
        <StatItem>
          <StatValue>{total.toLocaleString('pt-BR')}</StatValue>
          <StatLabel>Matrículas</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue accent>{valorTotal === null ? '-' : formatValorND(valorTotal)}</StatValue>
          <StatLabel>Valor ND</StatLabel>
        </StatItem>
      </StatsRow>
    </ResultadosContainer>
  )
}

const PageHeader = () => {
  return (
    <PageHeaderRow>
      <PageTitle>Alunos</PageTitle>
    </PageHeaderRow>
  )
}

const PROVEDOR_OPTIONS = [
  { value: 'EFI', label: 'EFI' },
  { value: 'HYPERPAY', label: 'Hyperpay' },
  { value: 'MERCADO_PAGO', label: 'Mercado Pago' },
]

const MEIO_PAGAMENTO_OPTIONS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CARTAO', label: 'Cartão' },
  { value: 'ISENTO', label: 'Isento' },
]

const FiltersSection = () => {
  const dispatch = AlunosStore.useDispatch()
  const { filters, cursosOptions, parceirosOptions } = AlunosStore.useState()
  const [filtroTextoInput, setFiltroTextoInput] = useState(filters.filtroTexto)
  const filtroTextoDebounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(
    () => () => {
      clearTimeout(filtroTextoDebounceRef.current)
    },
    []
  )

  const dateRangeValue: [Dayjs | null, Dayjs | null] | null =
    filters.dataInicio || filters.dataFim
      ? [
          filters.dataInicio ? dayjs(filters.dataInicio) : null,
          filters.dataFim ? dayjs(filters.dataFim) : null,
        ]
      : null

  const handleClearFilters = () => {
    clearTimeout(filtroTextoDebounceRef.current)
    setFiltroTextoInput('')
    dispatch(AlunosStore.actions.resetFilters())
    dispatch(AlunosStore.thunks.getAlunosData(INITIAL_FILTERS, { page: 1 }))
  }

  const handleFiltroTextoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtroTexto = e.target.value
    setFiltroTextoInput(filtroTexto)
    clearTimeout(filtroTextoDebounceRef.current)
    filtroTextoDebounceRef.current = setTimeout(() => {
      dispatch(AlunosStore.thunks.getAlunosData({ filtroTexto }, { page: 1 }))
    }, 300)
  }

  return (
    <FiltersContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 16, color: '#494d50', fontWeight: 600 }}>Filtros</span>
        <Button type="text" onClick={handleClearFilters}>
          <CloseOutlined /> Limpar filtros
        </Button>
      </div>
      <FiltersRow>
        <TextFieldSearch
          value={filtroTextoInput}
          placeholder="Pesquisar por CPF, nome, telefone, e-mail ou ID da matrícula"
          onChange={handleFiltroTextoChange}
        />

        <Select
          mode="multiple"
          allowClear
          placeholder="Status matrícula"
          style={{ minWidth: 200 }}
          value={filters.statusMatricula}
          onChange={(value: string[]) =>
            dispatch(AlunosStore.thunks.getAlunosData({ statusMatricula: value }, { page: 1 }))
          }
          options={STATUS_MATRICULA_OPTIONS}
          maxTagCount="responsive"
        />

        <Select
          mode="multiple"
          allowClear
          placeholder="Estado"
          style={{ minWidth: 130 }}
          value={filters.estados}
          onChange={(value: string[]) =>
            dispatch(AlunosStore.thunks.getAlunosData({ estados: value }, { page: 1 }))
          }
          options={UFS_BRASIL.map((uf) => ({ value: uf, label: uf }))}
          maxTagCount="responsive"
        />

        <Select
          mode="multiple"
          allowClear
          placeholder="Curso"
          style={{ minWidth: 220 }}
          value={filters.cursos}
          onChange={(value: number[]) =>
            dispatch(AlunosStore.thunks.getAlunosData({ cursos: value }, { page: 1 }))
          }
          showSearch
          optionFilterProp="label"
          options={cursosOptions.map((c) => ({
            value: c.id,
            label: `(${String(c.id).padStart(2, '0')}) ${c.nomeReduzido}`,
          }))}
          maxTagCount="responsive"
        />

        <Radio.Group
          value={filters.tipoVenda}
          onChange={(e) =>
            dispatch(
              AlunosStore.thunks.getAlunosData(
                { tipoVenda: e.target.value, parceiroIds: [] },
                { page: 1 }
              )
            )
          }
          optionType="button"
          buttonStyle="solid"
          options={[
            { label: 'Todos', value: 'todos' },
            { label: 'Venda Direta', value: 'venda_direta' },
            { label: 'Parceiros', value: 'parceiros' },
          ]}
        />

        {filters.tipoVenda === 'parceiros' && (
          <Select
            mode="multiple"
            allowClear
            placeholder="Selecionar parceiros"
            style={{ minWidth: 220 }}
            value={filters.parceiroIds}
            onChange={(value: number[]) =>
              dispatch(AlunosStore.thunks.getAlunosData({ parceiroIds: value }, { page: 1 }))
            }
            showSearch
            optionFilterProp="label"
            options={parceirosOptions.map((p) => ({ value: p.id, label: p.nome }))}
            maxTagCount="responsive"
          />
        )}

        <Select
          mode="multiple"
          allowClear
          placeholder="Provedor"
          style={{ minWidth: 150 }}
          value={filters.provedorPagamento}
          onChange={(value: string[]) =>
            dispatch(AlunosStore.thunks.getAlunosData({ provedorPagamento: value }, { page: 1 }))
          }
          options={PROVEDOR_OPTIONS}
          maxTagCount="responsive"
        />

        <Select
          mode="multiple"
          allowClear
          placeholder="Meio de pgto"
          style={{ minWidth: 150 }}
          value={filters.meioPagamento}
          onChange={(value: string[]) =>
            dispatch(AlunosStore.thunks.getAlunosData({ meioPagamento: value }, { page: 1 }))
          }
          options={MEIO_PAGAMENTO_OPTIONS}
          maxTagCount="responsive"
        />

        <RangePicker
          format="DD/MM/YYYY"
          locale={locale}
          value={dateRangeValue}
          disabledDate={(date) => date.isAfter(dayjs(), 'day')}
          onChange={(dates: [Dayjs | null, Dayjs | null] | null) => {
            const dataInicio = dates?.[0]?.format('YYYY-MM-DD') ?? null
            const dataFim = dates?.[1]?.format('YYYY-MM-DD') ?? null
            dispatch(AlunosStore.thunks.getAlunosData({ dataInicio, dataFim }, { page: 1 }))
          }}
          placeholder={['Data início', 'Data fim']}
        />

        <Radio.Group
          value={filters.tipoData}
          onChange={(e) =>
            dispatch(
              AlunosStore.thunks.getAlunosData(
                {
                  tipoData: e.target.value as 'createdAt' | 'dataPagamento',
                  statusPagamento: null,
                },
                { page: 1 }
              )
            )
          }
          optionType="button"
          buttonStyle="solid"
          options={[
            { label: 'Matrícula', value: 'createdAt' },
            { label: 'Pagamento', value: 'dataPagamento' },
          ]}
        />

        {filters.tipoData === 'createdAt' && (
          <Radio.Group value={filters.statusPagamento} optionType="button" buttonStyle="solid">
            <Radio.Button
              value="pago"
              onClick={() =>
                dispatch(
                  AlunosStore.thunks.getAlunosData(
                    { statusPagamento: filters.statusPagamento === 'pago' ? null : 'pago' },
                    { page: 1 }
                  )
                )
              }
            >
              Pago
            </Radio.Button>
            <Radio.Button
              value="pendente"
              onClick={() =>
                dispatch(
                  AlunosStore.thunks.getAlunosData(
                    { statusPagamento: filters.statusPagamento === 'pendente' ? null : 'pendente' },
                    { page: 1 }
                  )
                )
              }
            >
              Pendente
            </Radio.Button>
          </Radio.Group>
        )}
      </FiltersRow>
    </FiltersContainer>
  )
}

const TabelaContainer = () => (
  <TabelaWrapper>
    <Tabela />
  </TabelaWrapper>
)

const Tabela = () => {
  const { matriculas, loading, limit, page, total } = AlunosStore.useState()
  const dispatch = AlunosStore.useDispatch()

  const columns: ColumnsType<MatriculaModel> = [
    {
      title: 'Ids',
      dataIndex: 'id',
      width: 80,
      render: (_, matricula) => (
        <div style={{ fontSize: '.8em' }}>
          M:{' '}
          <CopyElement value={'' + matricula.id}>
            <strong>{matricula.id}</strong>
          </CopyElement>
          <br />
          A: <strong>{matricula.alunoId}</strong>
        </div>
      ),
    },
    // {
    //   title: 'CPF',
    //   key: 'cpf',
    //   width: 160,
    //   render: (_, matricula) => (

    //   ),
    // },
    {
      title: 'Aluno',
      key: 'nome',
      render: (_, matricula) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlunoInfoWrapper>
            <CpfInfo>
              <CopyElement value={matricula?.aluno?.cpf}>{matricula?.aluno?.cpf}</CopyElement>
            </CpfInfo>
            <Link
              to={`/cursos/${matricula?.curso?.id}/usuarios/${matricula?.aluno?.id}/acompanhamento?matriculaId=${matricula.id}`}
              style={{ textTransform: 'uppercase', fontWeight: 700, color: 'inherit' }}
            >
              {matricula?.aluno?.nome}
            </Link>
          </AlunoInfoWrapper>
          {matricula?.turmaId && (
            <Popover
              content={
                <div style={{ maxWidth: '300px' }}>
                  <p>
                    <strong>Turma:</strong> {matricula?.turmaId}
                  </p>
                  {matricula?.turma?.nome && (
                    <p>
                      <strong>Nome:</strong> {matricula?.turma?.nome}
                    </p>
                  )}
                  {matricula?.turma?.createdAt && (
                    <p>
                      <strong>Criada em:</strong>{' '}
                      {new Date(matricula?.turma?.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                  <a
                    href={`/turmas/${matricula?.turmaId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ver detalhes da turma
                  </a>
                </div>
              }
              title="Informações da Turma"
              trigger="hover"
            >
              <Badge
                count="T"
                style={{
                  backgroundColor: '#1890ff',
                  fontSize: '10px',
                  height: '18px',
                  lineHeight: '18px',
                  padding: '0 6px',
                  cursor: 'pointer',
                }}
              />
            </Popover>
          )}
        </div>
      ),
    },

    {
      title: 'Parceiro/Captador',
      key: 'parceiro',
      render: (_, matricula) => {
        const parceiroNome = matricula?.parceiro?.nome
        const parceiroPaiNome = matricula?.parceiro?.parceiroPai?.nome

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Show condition={parceiroNome}>
              <Tooltip title={parceiroNome}>
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '110px',
                    cursor: 'pointer',
                  }}
                >
                  {parceiroNome}
                </span>
              </Tooltip>
            </Show>
            <Show condition={parceiroPaiNome}>
              <Tooltip title={`Captador de ${parceiroPaiNome}`}>
                <small
                  style={{
                    color: '#666',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '110px',
                    cursor: 'pointer',
                  }}
                >
                  sob {parceiroPaiNome}
                </small>
              </Tooltip>
            </Show>
          </div>
        )
      },
    },
    {
      title: 'Curso',
      dataIndex: ['curso', 'nomeReduzido'],
      key: 'curso',
    },
    {
      title: 'Pagamento',
      key: 'pagamento_tags',
      width: 130,
      render: (_, matricula) => <PaymentTagsCell matricula={matricula} />,
    },
    {
      title: 'Valor ND',
      key: 'valor_nd',
      width: 120,
      render: (_, matricula) => <ValorNDCell matricula={matricula} />,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, matricula) => <StatusCell matricula={matricula} />,
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      key: 'uf',
      width: 60,
    },
  ]

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination
    dispatch(AlunosStore.thunks.getAlunosData({}, { page: current, limit: pageSize }))
  }

  return (
    <AntdTableWrapper>
      <Table
        columns={columns}
        dataSource={matriculas}
        loading={loading}
        rowKey={(record) => record.id}
        pagination={{
          current: page,
          pageSize: limit,
          total: total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
          pageSizeOptions: ['10', '15', '20', '50'],
        }}
        onChange={handleTableChange}
        style={{ width: '100%' }}
        rowClassName="custom-row"
      />
    </AntdTableWrapper>
  )
}

function PaymentTagsCell({ matricula }: { matricula: MatriculaModel }) {
  const { pagamento } = matricula
  if (!pagamento) return <span style={{ color: '#bbb', fontSize: 12 }}>—</span>

  const provedorLogos: Record<string, string> = {
    EFI: efiLogo,
    HYPERPAY: hyperpayLogo,
    MERCADO_PAGO: mercadoPagoLogo,
  }

  const provedorLabels: Record<string, string> = {
    EFI: 'EFI',
    HYPERPAY: 'Hyperpay',
    MERCADO_PAGO: 'Mercado Pago',
  }

  const meioColors: Record<string, string> = {
    PIX: 'cyan',
    BOLETO: 'gold',
    CARTAO: 'geekblue',
    ISENTO: 'default',
  }

  const meioLabels: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CARTAO: 'Cartão',
    ISENTO: 'Isento',
    BAIXA_REP_PILOTAR: 'Saldo REP',
  }

  return (
    <PaymentTagsWrapper>
      {pagamento.provedorPagamento && provedorLogos[pagamento.provedorPagamento] && (
        <Tooltip title={provedorLabels[pagamento.provedorPagamento]}>
          <img
            src={provedorLogos[pagamento.provedorPagamento]}
            alt={provedorLabels[pagamento.provedorPagamento]}
            style={{ height: 18, objectFit: 'contain', display: 'block' }}
          />
        </Tooltip>
      )}
      {pagamento.meioPagamento && (
        <Tag color={meioColors[pagamento.meioPagamento]} style={{ margin: 0 }}>
          {meioLabels[pagamento.meioPagamento]}
        </Tag>
      )}
    </PaymentTagsWrapper>
  )
}

function ValorNDCell({ matricula }: { matricula: MatriculaModel }) {
  const { pagamento } = matricula
  if (!pagamento) return <span style={{ color: '#bbb', fontSize: 12 }}>—</span>

  const valorND = pagamento.valorNewDriver ?? pagamento.valor
  const isPago = !!pagamento.dataPagamento

  const formatCurrency = (value?: number | null) =>
    value != null
      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
      : '—'

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return null
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const popoverContent = (
    <ValorPopoverContent>
      <ValorPopoverRow>
        <ValorPopoverLabel>Valor original:</ValorPopoverLabel>
        <span>{formatCurrency(pagamento.valorOriginal)}</span>
      </ValorPopoverRow>
      <ValorPopoverRow>
        <ValorPopoverLabel>Valor ND:</ValorPopoverLabel>
        <strong>{formatCurrency(valorND)}</strong>
      </ValorPopoverRow>
      {pagamento.cupom && (
        <ValorPopoverRow>
          <ValorPopoverLabel>Cupom:</ValorPopoverLabel>
          <Tag color="purple" style={{ margin: 0 }}>
            {pagamento.cupom.codigo}
          </Tag>
        </ValorPopoverRow>
      )}
      {matricula.parceiro?.nome && (
        <ValorPopoverRow>
          <ValorPopoverLabel>Parceiro:</ValorPopoverLabel>
          <span>{matricula.parceiro.nome}</span>
        </ValorPopoverRow>
      )}
      {matricula.parceiro?.parceiroPai?.nome && (
        <ValorPopoverRow>
          <ValorPopoverLabel>Parceiro pai:</ValorPopoverLabel>
          <span>{matricula.parceiro.parceiroPai.nome}</span>
        </ValorPopoverRow>
      )}
      <ValorPopoverRow>
        <ValorPopoverLabel>Data da matrícula:</ValorPopoverLabel>
        <span>{format(new Date(matricula.createdAt), 'dd/MM/yyyy HH:mm')}</span>
      </ValorPopoverRow>
      <ValorPopoverRow>
        <ValorPopoverLabel>Data pagamento:</ValorPopoverLabel>
        <span>
          {format(new Date(pagamento.dataPagamento), 'dd/MM/yyyy HH:mm') ?? (
            <em style={{ color: '#bbb' }}>Pendente</em>
          )}
        </span>
      </ValorPopoverRow>
    </ValorPopoverContent>
  )

  return (
    <Popover content={popoverContent} title="Detalhes do pagamento" trigger="hover">
      <ValorNDWrapper isPago={isPago}>
        <ValorNDAmount>{formatCurrency(valorND)}</ValorNDAmount>
        {isPago && (
          <PaidBadge>
            <CheckCircleFilled />
          </PaidBadge>
        )}
      </ValorNDWrapper>
    </Popover>
  )
}

function StatusCell({ matricula }: { matricula: MatriculaModel }) {
  const getModuleProgress = (moduloId: number) => {
    const estatisticasDoModulo = matricula.estatisticasEstudos?.filter(
      (estatistica) => estatistica.moduloId === moduloId
    )

    if (!estatisticasDoModulo?.length) return 0
    if (estatisticasDoModulo.some((estatistica) => estatistica.moduloCompleto)) return 100

    return Math.min(
      100,
      Math.max(
        0,
        ...estatisticasDoModulo.map(
          (estatistica) => Number(estatistica.percentualProgressoMaisAvancado) || 0
        )
      )
    )
  }

  const calculateProgress = () => {
    const modulos = matricula?.curso?.modulos ?? []
    if (!modulos.length) return 0

    const duracaoTotal = modulos.reduce((total, modulo) => total + (Number(modulo.duracao) || 0), 0)
    const progresso = duracaoTotal
      ? modulos.reduce((total, modulo) => {
          return total + getModuleProgress(modulo.id) * (Number(modulo.duracao) || 0)
        }, 0) / duracaoTotal
      : modulos.reduce((total, modulo) => total + getModuleProgress(modulo.id), 0) / modulos.length

    return Math.min(100, Math.max(0, Math.round(progresso)))
  }

  const getCurrentModule = () => {
    if (!matricula?.curso?.modulos || !matricula?.estatisticasEstudos?.length) return 'Não iniciado'

    const modulos = [...matricula.curso.modulos].sort((a, b) => a.ordenacao - b.ordenacao)

    const currentModule = modulos.find((modulo) => {
      const progresso = getModuleProgress(modulo.id)
      return progresso > 0 && progresso < 100
    })

    if (currentModule) return currentModule.nome

    const allModulesCompleted = modulos.every((modulo) => getModuleProgress(modulo.id) === 100)
    if (allModulesCompleted) return 'Todos os módulos concluídos'

    const nextModule = modulos.find((modulo) => getModuleProgress(modulo.id) < 100)
    return nextModule?.nome || 'Primeiro módulo'
  }

  const getStatusInfo = () => {
    const progress = calculateProgress()

    const statusColors: Record<string, string> = {
      AGUARDANDO_MATRICULA: '#fa8c16',
      FINALIZADO: 'green',
      MATRICULADO: 'geekblue',
      AGUARDANDO_ENVIO: '#722ed1',
      AGUARDANDO_MATRICULA_MODULO_PRATICO: '#3a3f10',
      AGUARDANDO_CONCLUSAO_MODULO_PRATICO: '#b700ff',
    }

    const color = statusColors[matricula?.statusDetran] || '#444'
    const text = matricula?.statusDetran?.replace(/_/g, ' ') || 'Sem status'

    if (matricula?.statusDetran === 'FINALIZADO') return { color, text, progress: 100 }
    if (matricula?.statusDetran === 'AGUARDANDO_MATRICULA') return { color, text, progress: 0 }

    return { color, text, progress }
  }

  const formatDate = (date: string | Date | null) => {
    if (!date) return null
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const statusInfo = getStatusInfo()
  const currentModule = getCurrentModule()
  const progress = statusInfo.progress

  return (
    <Popover
      content={
        <div style={{ minWidth: '280px' }}>
          <p>
            <strong>Data de matrícula:</strong>
            <br />
            {formatDate(matricula.createdAt)}
          </p>
          <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
          <CertificateEmissionDate matricula={matricula} />
          <div style={{ marginTop: '12px' }}>
            <strong>Módulo atual:</strong>
            <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', margin: '4px 0' }}>
              {currentModule}
            </p>
            <div
              style={{
                width: '100%',
                height: '17px',
                borderRadius: '4px',
                background: '#d9d9d9',
                padding: '3px',
                marginTop: '8px',
              }}
            >
              <div
                style={{
                  height: '100%',
                  borderRadius: '4px',
                  background: progress === 100 ? '#399b39' : '#F9A825',
                  width: `${progress}%`,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <p style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
              {progress}% concluído
            </p>
          </div>
        </div>
      }
      title="Informações da Matrícula"
      trigger="hover"
    >
      <div style={{ cursor: 'pointer' }}>
        <Tag color={statusInfo.color} style={{ textTransform: 'capitalize' }}>
          {statusInfo.text.toLowerCase()}
        </Tag>
        <Show condition={matricula?.statusDetran === 'MATRICULADO'}>
          <div
            style={{
              width: '100%',
              height: '8px',
              borderRadius: '2px',
              background: '#d9d9d9',
              padding: '2px',
              marginTop: 4,
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: '2px',
                background: progress === 100 ? '#399b39' : '#F9A825',
                width: `${progress}%`,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </Show>
      </div>
    </Popover>
  )
}

const CertificateEmissionDate = ({ matricula }: { matricula: MatriculaModel }) => {
  if (matricula.statusDetran !== 'MATRICULADO') return null
  if (!matricula.curso?.diasParaEmissaoCertificado) return null
  if (!matricula.dataInicioCurso) return null

  const ufMatricula = matricula.ufMatriculaDetran ?? matricula.uf
  const ajustePrazo = ['SP', 'RS'].includes(ufMatricula) ? 0 : 1
  const diasParaEmissao = Math.max(0, matricula.curso.diasParaEmissaoCertificado - ajustePrazo)
  const certificateDate = addDays(startOfDay(new Date(matricula.dataInicioCurso)), diasParaEmissao)

  return (
    <div style={{ marginTop: '8px' }}>
      <strong>Certificado disponível em:</strong>
      <p style={{ fontSize: '12px', color: '#666', margin: '4px 0' }}>
        {format(certificateDate, 'dd/MM/yyyy')} (
        {formatDistance(certificateDate, new Date(), { locale: ptBR, addSuffix: true })})
      </p>
    </div>
  )
}

/* ─── Styled components ─── */

const PageLayout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`

const PageTitle = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const FiltersContainer = styled.div`
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
`

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const ResultadosContainer = styled.div`
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 160px;
`

const StatsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: space-between;
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const StatValue = styled.span<{ accent?: boolean }>`
  font-size: 22px;
  font-weight: 700;
  color: ${({ accent }) => (accent ? '#1677ff' : '#333')};
  line-height: 1.2;
`

const StatLabel = styled.span`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const TabelaWrapper = styled.div`
  padding: 8px;
  background: #ffffff;
  border-radius: 10px;
  width: 100%;
  min-width: 800px;
`

const AntdTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #585858;
    font-weight: normal;
    border-bottom: 1px solid #e7e3ec;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e7e3ec;
    padding: 8px 16px;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5;
  }

  .ant-table-pagination {
    margin: 16px 0;
  }

  .ant-table-container {
    min-width: 1200px;
  }
`

const PaymentTagsWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: flex-start;
`

const ValorNDWrapper = styled.div<{ isPago: boolean }>`
  cursor: pointer;
  display: flex;
  gap: 2px;
  font-weight: 500;
  color: #333;
  ${({ isPago }) =>
    isPago &&
    `
    font-weight: 600;
  `}
`

const ValorNDAmount = styled.span`
  font-size: 13px;
`

const PaidBadge = styled.span`
  color: #52c41a;
`

const ValorPopoverContent = styled.div`
  display: grid;
  gap: 6px;
  min-width: 220px;
`

const ValorPopoverRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 13px;
`

const ValorPopoverLabel = styled.span`
  color: #888;
  white-space: nowrap;
`

const AlunoInfoWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 2px;

  @media (min-width: 1600px) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
`

const CpfInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  @media (min-width: 1600px) {
    width: 160px;
    flex-shrink: 0;
  }
`
